/**
 * Shared placement helpers for Tooltip / InfoTooltip (SaaS: anchor to element, not cursor).
 */

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export type TooltipCoords = {
  top: number;
  left: number;
  placement: TooltipPosition;
};

export const TOOLTIP_GAP = 8;
export const TOOLTIP_VIEWPORT_PAD = 8;

/** Prefer CSS interaction queries so touchscreen laptops with a mouse still get hover tooltips. */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    return false;
  }
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore older browsers
    navigator.msMaxTouchPoints > 0
  );
}

/** First element child under a wrapper (skips text nodes). */
export function getAnchorElement(wrapper: HTMLElement): HTMLElement {
  for (let i = 0; i < wrapper.childNodes.length; i++) {
    const node = wrapper.childNodes[i];
    if (node.nodeType === Node.ELEMENT_NODE) {
      return node as HTMLElement;
    }
  }
  return wrapper;
}

export function resolvePlacement(
  preferred: TooltipPosition,
  rect: DOMRect,
  need = 40
): TooltipPosition {
  const space = {
    top: rect.top,
    bottom: window.innerHeight - rect.bottom,
    left: rect.left,
    right: window.innerWidth - rect.right,
  };

  if (preferred === 'bottom' && space.bottom < need && space.top > space.bottom) {
    return 'top';
  }
  if (preferred === 'top' && space.top < need && space.bottom > space.top) {
    return 'bottom';
  }
  if (preferred === 'left' && space.left < need && space.right > space.left) {
    return 'right';
  }
  if (preferred === 'right' && space.right < need && space.left > space.right) {
    return 'left';
  }
  return preferred;
}

export function coordsForPlacement(
  placement: TooltipPosition,
  rect: DOMRect,
  options?: {
    gap?: number;
    viewportPad?: number;
    /** Half-width used to keep a wide bubble fully on-screen (InfoTooltip). */
    bubbleHalfWidth?: number;
  }
): TooltipCoords {
  const gap = options?.gap ?? TOOLTIP_GAP;
  const viewportPad = options?.viewportPad ?? TOOLTIP_VIEWPORT_PAD;
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;

  let top: number;
  let left: number;

  switch (placement) {
    case 'top':
      top = rect.top - gap;
      left = centerX;
      break;
    case 'left':
      top = centerY;
      left = rect.left - gap;
      break;
    case 'right':
      top = centerY;
      left = rect.right + gap;
      break;
    case 'bottom':
    default:
      top = rect.bottom + gap;
      left = centerX;
      break;
  }

  if (options?.bubbleHalfWidth != null) {
    const half = options.bubbleHalfWidth;
    const minLeft = half + viewportPad;
    const maxLeft = window.innerWidth - half - viewportPad;
    left = Math.max(minLeft, Math.min(centerX, maxLeft));
  } else {
    left = Math.max(viewportPad, Math.min(left, window.innerWidth - viewportPad));
  }

  top = Math.max(viewportPad, Math.min(top, window.innerHeight - viewportPad));

  return { top, left, placement };
}

export function transformForPlacement(placement: TooltipPosition): string {
  switch (placement) {
    case 'top':
      return 'translate(-50%, -100%)';
    case 'left':
      return 'translate(-100%, -50%)';
    case 'right':
      return 'translate(0, -50%)';
    case 'bottom':
    default:
      return 'translate(-50%, 0)';
  }
}
